import React, { useState, useRef } from "react";
import { Box, Typography, Button } from "@mui/material";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import { useDispatch } from "react-redux";
import theme, { textClasses, buttonClasses } from "../../../static/styles/theme";
import { uploadDocumentFile } from "../../../redux/actions";
import AlertDialog from "../DialogBox/dialogbox";

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/webp"];


const GenericAddImageModal = ({
    open = false,
    onClose,
    onSuccess,
    moduleName = "",
    documentId = "",
    fieldId = "",
}) => {
    const dispatch = useDispatch();
    const fileRef = useRef();
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [dragging, setDragging] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [error, setError] = useState("");

    const reset = () => {
        setFile(null);
        setPreview("");
        setError("");
        setDragging(false);
        setUploading(false);
        if (fileRef.current) fileRef.current.value = "";
    };

    const handleClose = () => {
        reset();
        onClose?.();
    };

    const validateAndSet = (selected) => {
        setError("");
        if (!selected) return;
        if (!ALLOWED_TYPES.includes(selected.type)) {
            setError("Only jpg, jpeg, png, webp files are allowed.");
            return;
        }

        const reader = new FileReader();
        reader.onload = () => {
            setFile(selected);
            setPreview(reader.result);
        };
        reader.readAsDataURL(selected);
    };

    const handleFileChange = (e) => validateAndSet(e.target.files?.[0]);

    const handleDrop = (e) => {
        e.preventDefault();
        setDragging(false);
        validateAndSet(e.dataTransfer.files?.[0]);
    };

    const handleUpload = () => {
        if (!file || !documentId || !fieldId) return;
        setUploading(true);
        const reader = new FileReader();
        reader.onload = () => {
            dispatch(uploadDocumentFile(
                { moduleName, documentId, fieldId, file: reader.result },
                ({ success }) => {
                    setUploading(false);
                    if (success) { handleClose(); onSuccess?.(); }
                }
            ));
        };
        reader.readAsDataURL(file);
    };

    return (
        <AlertDialog
            viewDialog={open}
            handleClose={handleClose}
            title="Upload Image"
            viewCloseIcon={true}
            width="420px"
            actionsCSS={{ justifyContent: "flex-start" }}
            bodyCSS={{ p: 0, pt: 0 }}
            body={
                <>

                    {/* Drop zone */}
                    <Box
                        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                        onDragLeave={() => setDragging(false)}
                        onDrop={handleDrop}
                        onClick={() => !file && fileRef.current?.click()}
                        sx={{
                            border: `2px dashed ${dragging ? theme.themeOrange : theme.themeBlack}`,
                            borderRadius: "8px",
                            p: 3,
                            textAlign: "center",
                            cursor: file ? "default" : "pointer",
                            transition: "border-color 0.2s",
                            backgroundColor: dragging ? `${theme.themeOrange}0d` : "transparent",
                            minHeight: "140px",
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                        }}
                    >
                        {preview ? (
                            <Box sx={{ position: "relative", width: "100%" }}>
                                <img
                                    src={preview}
                                    alt="preview"
                                    style={{
                                        maxWidth: "100%",
                                        maxHeight: "180px",
                                        borderRadius: "4px",
                                        objectFit: "contain",
                                        display: "block",
                                        margin: "0 auto",
                                    }}
                                />
                                <Typography
                                    sx={{ ...textClasses.t12n, color: theme.themeOrange, mt: 1, cursor: "pointer" }}
                                    onClick={(e) => { e.stopPropagation(); reset(); }}
                                >
                                    Remove
                                </Typography>
                            </Box>
                        ) : (
                            <>
                                <CloudUploadOutlinedIcon sx={{ fontSize: "40px", color: theme.themeOrange, mb: 1 }} />
                                <Typography sx={{ ...textClasses.t13n, color: theme.themeBlack }}>
                                    Drop images here
                                </Typography>
                                <Typography sx={{ ...textClasses.t12n, color: theme.themeLabel, mt: 0.5 }}>
                                    or{" "}
                                    <span
                                        style={{ color: theme.themeOrange, textDecoration: "underline", cursor: "pointer" }}
                                        onClick={(e) => { e.stopPropagation(); fileRef.current?.click(); }}
                                    >
                                        browse files
                                    </span>
                                </Typography>
                            </>
                        )}
                    </Box>

                    <input
                        hidden
                        ref={fileRef}
                        type="file"
                        accept={ALLOWED_TYPES.join(",")}
                        onChange={handleFileChange}
                    />

                    {error && (
                        <Typography sx={{ ...textClasses.t12n, color: theme.themeRed, mt: 0.5 }}>
                            {error}
                        </Typography>
                    )}
                </>
            }
        >
            <Button
                variant="outlined"
                onClick={handleClose}
                disabled={uploading}
                sx={{ ...buttonClasses.lynkitOrangeEmpty, m: 1, fontSize: ".7rem", minWidth: "130px" }}
            >
                Cancel
            </Button>
            <Button
                variant="outlined"
                onClick={handleUpload}
                disabled={!file || uploading}
                sx={{ ...buttonClasses.lynkitOrangeFill, m: 1, fontSize: ".7rem", minWidth: "130px" }}
            >
                {uploading ? "Uploading..." : "Upload"}
            </Button>
        </AlertDialog>
    );
};

export default GenericAddImageModal;
